import { useEffect, useRef } from "react";
import { Printer, Bluetooth } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import Barcode from "@/components/Barcode";
import { usePrinter } from "@/context/PrinterContext";

/**
 * Shared product-label preview + print dialog.
 *
 * Pops a small printable label (recipe, batch, packs, packed date, label
 * code, allergen banner, barcode). Two print paths:
 *   1) Bluetooth thermal printer (preferred, when paired) — streams TSPL
 *      commands straight to the printer; no OS print dialog.
 *   2) Browser `window.print()` fallback — used when no printer is paired
 *      or Web Bluetooth isn't supported.
 *
 * When `autoPrint` is true (used after "Complete batch") we kick whichever
 * path is available so the operator never has to chase an extra click.
 */
export default function LabelDialog({ packing, autoPrint = false, onClose }) {
  const fired = useRef(false);
  const open = !!packing;
  const printer = usePrinter();
  const btReady = printer?.isConnected;

  // Run the print once per dialog open.
  useEffect(() => {
    if (!open || !autoPrint || fired.current || !packing) return;
    fired.current = true;
    const t = setTimeout(() => {
      if (btReady) {
        printer.printPackingLabel(packing);
      } else {
        // Wait for the barcode SVG to render before the browser print
        // dialog snapshots the page.
        window.print();
      }
    }, 350);
    return () => clearTimeout(t);
  }, [open, autoPrint, btReady, packing, printer]);

  useEffect(() => {
    if (!open) fired.current = false;
  }, [open]);

  const handlePrint = () => {
    if (btReady && packing) {
      printer.printPackingLabel(packing);
    } else {
      window.print();
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-md" data-testid="label-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Product label</DialogTitle>
        </DialogHeader>
        {packing && (
          <div className="print-area bg-white p-6 border border-border rounded-lg">
            <div className="text-[10px] uppercase tracking-[0.22em] text-stone-500 font-bold">
              Oz Bakery · Wholesale Bakery
            </div>
            <div className="font-display text-2xl font-bold mt-1">
              {packing.recipe_name}
            </div>
            <div className="grid grid-cols-2 gap-2 text-sm mt-3 mb-3">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-stone-500">Batch</div>
                <div className="font-mono">{packing.batch_code}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-stone-500">Packs</div>
                <div className="tnum">
                  {packing.packs_produced} {packing.pack_unit}
                </div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-stone-500">Packed</div>
                <div className="tnum">{packing.packed_at?.slice(0, 10)}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-stone-500">Label code</div>
                <div className="font-mono">{packing.label_code}</div>
              </div>
            </div>
            {packing.allergens?.length > 0 && (
              <div className="mb-3 p-2 border-2 border-amber-400 rounded bg-amber-50">
                <div className="text-[10px] uppercase tracking-wider text-amber-800 font-bold">
                  Contains
                </div>
                <div className="text-sm font-semibold text-amber-900">
                  {packing.allergens.join(" · ")}
                </div>
              </div>
            )}
            <div className="flex justify-center">
              <Barcode value={packing.label_code} />
            </div>
          </div>
        )}
        <DialogFooter>
          <Button onClick={handlePrint} data-testid="print-button">
            {btReady ? (
              <>
                <Bluetooth className="h-4 w-4 mr-2" /> Print to {printer.deviceName || "printer"}
              </>
            ) : (
              <>
                <Printer className="h-4 w-4 mr-2" /> Print
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
